import React, { useState, useEffect, useCallback } from 'react';
import { User, Bell, Lock, Mail, Phone, MapPin, Save } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/layout/Layout';
import mockApi from '../services/mockApi';

const ClienteConfiguracionPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [guardado, setGuardado] = useState(false);

  const [datosPersonales, setDatosPersonales] = useState({
    nombre: '',
    email: '',
    telefono: '',
    direccion: '',
    ciudad: '',
    codigoPostal: ''
  });

  const [preferenciasNotificaciones, setPreferenciasNotificaciones] = useState({
    email: true,
    sms: false,
    push: true,
    averias: true,
    vencimiento: true,
    documentos: true,
    promociones: false
  });

  const [consentimientos, setConsentimientos] = useState({
    terminosCondiciones: true,
    politicaPrivacidad: true,
    comunicacionesComerciales: false,
    cesionDatos: false
  });

  const cargarDatos = useCallback(async () => {
    try {
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      const garantiaData = await mockApi.getGarantiaCliente(clienteRes.data.id);
      if (garantiaData?.cliente) {
        setDatosPersonales({
          nombre: garantiaData.cliente.nombre || '',
          email: garantiaData.cliente.email || '',
          telefono: garantiaData.cliente.telefono || '',
          direccion: garantiaData.cliente.direccion || '',
          ciudad: garantiaData.cliente.ciudad || '',
          codigoPostal: garantiaData.cliente.codigoPostal || ''
        });
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
    }
  }, [user.email]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleGuardarPreferencias = async () => {
    try {
      setLoading(true);
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      await mockApi.actualizarPreferenciasNotificaciones(clienteRes.data.id, preferenciasNotificaciones);
      setGuardado(true);
      setTimeout(() => setGuardado(false), 3000);
    } catch (error) {
      console.error('Error al guardar preferencias:', error);
      alert('Error al guardar las preferencias');
    } finally {
      setLoading(false);
    }
  };

  const handleGuardarConsentimientos = async () => {
    try {
      setLoading(true);
      const clienteRes = await mockApi.getClienteByEmail(user.email);
      await mockApi.actualizarConsentimiento(clienteRes.data.id, consentimientos);
      setGuardado(true);
      setTimeout(() => setGuardado(false), 3000);
    } catch (error) {
      console.error('Error al guardar consentimientos:', error);
      alert('Error al guardar los consentimientos');
    } finally {
      setLoading(false);
    }
  };

  return (
    // Apertura del Layout
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              Configuración
            </h1>
            <p className="text-gray-600 mt-1">Gestiona tus preferencias y datos personales</p>
          </div>
        </div>

        {guardado && (
          <div className="glass-effect rounded-2xl p-4 border-l-4 border-green-500 bg-green-50/50">
            <div className="flex items-center gap-3">
              <Save className="w-5 h-5 text-green-600" />
              <p className="font-bold text-green-900">Cambios guardados correctamente</p>
            </div>
          </div>
        )}

        <div className="glass-effect rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Datos Personales</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Nombre Completo
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={datosPersonales.nombre}
                  disabled
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={datosPersonales.email}
                  disabled
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Teléfono
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="tel"
                  value={datosPersonales.telefono}
                  disabled
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Ciudad
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={datosPersonales.ciudad}
                  disabled
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-600"
                />
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Dirección
              </label>
              <input
                type="text"
                value={datosPersonales.direccion}
                disabled
                className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-600"
              />
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
            <p className="text-sm text-blue-700">
              <strong>Nota:</strong> Para modificar tus datos personales, por favor contacta con nuestro servicio de atención al cliente.
            </p>
          </div>
        </div>

        <div className="glass-effect rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-warning-500 to-warning-600 rounded-xl flex items-center justify-center">
              <Bell className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Preferencias de Notificaciones</h2>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="font-bold text-gray-900 mb-4">Canales de Comunicación</h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="font-semibold text-gray-900">Email</p>
                      <p className="text-sm text-gray-600">Recibir notificaciones por correo electrónico</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferenciasNotificaciones.email}
                    onChange={(e) => setPreferenciasNotificaciones({
                      ...preferenciasNotificaciones,
                      email: e.target.checked
                    })}
                    className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="font-semibold text-gray-900">SMS</p>
                      <p className="text-sm text-gray-600">Recibir notificaciones por mensaje de texto</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferenciasNotificaciones.sms}
                    onChange={(e) => setPreferenciasNotificaciones({
                      ...preferenciasNotificaciones,
                      sms: e.target.checked
                    })}
                    className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
                  <div className="flex items-center gap-3">
                    <Bell className="w-5 h-5 text-gray-600" />
                    <div>
                      <p className="font-semibold text-gray-900">Notificaciones Push</p>
                      <p className="text-sm text-gray-600">Recibir notificaciones en el navegador</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferenciasNotificaciones.push}
                    onChange={(e) => setPreferenciasNotificaciones({
                      ...preferenciasNotificaciones,
                      push: e.target.checked
                    })}
                    className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                  />
                </label>
              </div>
            </div>

            <div>
              <h3 className="font-bold text-gray-900 mb-4">Tipos de Notificaciones</h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
                  <div>
                    <p className="font-semibold text-gray-900">Averías</p>
                    <p className="text-sm text-gray-600">Actualizaciones sobre el estado de tus averías</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferenciasNotificaciones.averias}
                    onChange={(e) => setPreferenciasNotificaciones({
                      ...preferenciasNotificaciones,
                      averias: e.target.checked
                    })}
                    className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
                  <div>
                    <p className="font-semibold text-gray-900">Vencimiento de Garantía</p>
                    <p className="text-sm text-gray-600">Alertas cuando tu garantía esté próxima a vencer</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferenciasNotificaciones.vencimiento}
                    onChange={(e) => setPreferenciasNotificaciones({
                      ...preferenciasNotificaciones,
                      vencimiento: e.target.checked
                    })}
                    className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
                  <div>
                    <p className="font-semibold text-gray-900">Documentos</p>
                    <p className="text-sm text-gray-600">Notificaciones sobre nuevos documentos disponibles</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferenciasNotificaciones.documentos}
                    onChange={(e) => setPreferenciasNotificaciones({
                      ...preferenciasNotificaciones,
                      documentos: e.target.checked
                    })}
                    className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                  />
                </label>

                <label className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
                  <div>
                    <p className="font-semibold text-gray-900">Promociones</p>
                    <p className="text-sm text-gray-600">Ofertas y promociones especiales</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferenciasNotificaciones.promociones}
                    onChange={(e) => setPreferenciasNotificaciones({
                      ...preferenciasNotificaciones,
                      promociones: e.target.checked
                    })}
                    className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                  />
                </label>
              </div>
            </div>

            <button
              onClick={handleGuardarPreferencias}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:shadow-lg transition-all duration-300 transform hover:scale-105 font-bold disabled:opacity-50"
            >
              <Save className="w-5 h-5" />
              <span>{loading ? 'Guardando...' : 'Guardar Preferencias'}</span>
            </button>
          </div>
        </div>

        <div className="glass-effect rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-xl flex items-center justify-center">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Privacidad y Consentimientos</h2>
          </div>

          <div className="space-y-3">
            <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
              <input
                type="checkbox"
                checked={consentimientos.terminosCondiciones}
                disabled
                className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500 mt-1"
              />
              <div>
                <p className="font-semibold text-gray-900">Términos y Condiciones</p>
                <p className="text-sm text-gray-600">He leído y acepto los términos y condiciones del servicio</p>
              </div>
            </label>

            <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
              <input
                type="checkbox"
                checked={consentimientos.politicaPrivacidad}
                disabled
                className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500 mt-1"
              />
              <div>
                <p className="font-semibold text-gray-900">Política de Privacidad</p>
                <p className="text-sm text-gray-600">He leído y acepto la política de privacidad</p>
              </div>
            </label>

            <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
              <input
                type="checkbox"
                checked={consentimientos.comunicacionesComerciales}
                onChange={(e) => setConsentimientos({
                  ...consentimientos,
                  comunicacionesComerciales: e.target.checked
                })}
                className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500 mt-1"
              />
              <div>
                <p className="font-semibold text-gray-900">Comunicaciones Comerciales</p>
                <p className="text-sm text-gray-600">Acepto recibir comunicaciones comerciales y promocionales</p>
              </div>
            </label>

            <label className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer">
              <input
                type="checkbox"
                checked={consentimientos.cesionDatos}
                onChange={(e) => setConsentimientos({
                  ...consentimientos,
                  cesionDatos: e.target.checked
                })}
                className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500 mt-1"
              />
              <div>
                <p className="font-semibold text-gray-900">Cesión de Datos</p>
                <p className="text-sm text-gray-600">Acepto la cesión de mis datos a terceros colaboradores</p>
              </div>
            </label>
          </div>

          <button
            onClick={handleGuardarConsentimientos}
            disabled={loading}
            className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white rounded-xl hover:shadow-lg transition-all duration-300 transform hover:scale-105 font-bold disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            <span>{loading ? 'Guardando...' : 'Guardar Consentimientos'}</span>
          </button>
        </div>

        <div className="glass-effect rounded-2xl p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
          <div className="flex items-start gap-4">
            <Lock className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-blue-900 mb-2">Protección de Datos</h3>
              <p className="text-sm text-blue-700">
                Tus datos están protegidos según el Reglamento General de Protección de Datos (RGPD).
                Puedes ejercer tus derechos de acceso, rectificación, supresión, limitación, portabilidad
                y oposición contactando con nuestro Delegado de Protección de Datos en: dpo@rdgarantia.com
              </p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ClienteConfiguracionPage;
